import React, { useCallback, useEffect, useState } from "react";
import { appVersionApi } from "@/services/phase1Api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tag, ArrowUpCircle } from "lucide-react";
import { toast } from "sonner";

export default function ReleaseVersionSettings({ session }) {
  const [data, setData] = useState(null);
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    appVersionApi.adminGet(session).then((d) => { setData(d); setManual(d?.version || ""); }).catch(() => {});
  }, [session]);
  useEffect(() => { load(); }, [load]);

  const bump = async () => {
    setBusy("bump");
    try { const d = await appVersionApi.bump(session); setData(d); setManual(d.version); toast.success(`Released v${d.version}`); window.dispatchEvent(new CustomEvent("app-version-changed", { detail: d.version })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not bump version"); }
    finally { setBusy(""); }
  };
  const save = async () => {
    const v = (manual || "").trim();
    if (!v) { toast.error("Enter a version number"); return; }
    setBusy("save");
    try { const d = await appVersionApi.set(v, session); setData(d); toast.success(`Set release to v${d.version}`); window.dispatchEvent(new CustomEvent("app-version-changed", { detail: d.version })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invalid version"); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="release-version-settings" className="max-w-2xl">
      <div className="rounded-xl border border-border bg-background p-6">
        <div className="flex items-center gap-2 mb-1">
          <Tag className="h-4 w-4 text-foreground/50" />
          <h2 className="text-sm font-semibold text-foreground">Release Version</h2>
        </div>
        <p className="text-xs text-foreground/50 mb-5">
          The current release number shown across the platform. Bump it with each release, or set a whole-number release (e.g. 2.0) manually.
        </p>

        <div className="flex items-center gap-3 mb-6">
          <div className="text-[11px] uppercase tracking-wide text-foreground/40">Current</div>
          <div className="text-3xl font-bold text-foreground" data-testid="current-release-version">v{data?.version || "—"}</div>
        </div>

        <div className="flex flex-wrap items-end gap-3 border-t border-border pt-5">
          <Button onClick={bump} disabled={busy === "bump"} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="bump-version-btn">
            <ArrowUpCircle className="h-4 w-4 mr-1.5" />
            {busy === "bump" ? "Releasing…" : "Bump to next release"}
          </Button>
          <div className="flex items-end gap-2">
            <div>
              <div className="text-[11px] text-foreground/50 mb-1">Set specific version</div>
              <Input value={manual} onChange={(e) => setManual(e.target.value)} className="h-9 w-32" placeholder="2.0" data-testid="version-input" />
            </div>
            <Button variant="outline" onClick={save} disabled={busy === "save"} className="h-9" data-testid="set-version-btn">Set</Button>
          </div>
        </div>
      </div>

      {data?.history?.length > 0 && (
        <div className="rounded-xl border border-border bg-background p-6 mt-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Release history</h3>
          <ul className="space-y-1.5" data-testid="version-history">
            {[...data.history].reverse().slice(0, 20).map((h, i) => (
              <li key={i} className="flex items-center justify-between text-[13px]">
                <span className="font-medium text-foreground">v{h.version}</span>
                <span className="text-foreground/40">{h.note} · {String(h.at).slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
